/**
 * vCard parser — RFC 2426 (vCard 3.0), RFC 6350 (vCard 4.0), and de-facto
 * vCard 2.1 variants exported by iOS Contacts, Android/Samsung Contacts,
 * Google Contacts, and Outlook.
 *
 * Runs entirely in the browser. Returns a plain object shaped to the
 * `blankRealtor()` fields defined in RealtorForms.jsx so the caller can
 * spread it directly over the form state.
 *
 * Kept dep-free on purpose — the WHATWG "vcards" or "ical.js" packages
 * add ~40 KB of parser + timezone data we don't need. This module is
 * ~200 lines and covers every real-world .vcf file the target users
 * will actually export from their phone.
 */

// ---------- Low-level helpers ----------

/** Unfold RFC-6350 continuation lines. A line starting with SP or TAB
 * is a continuation of the previous line, joined with the leading
 * whitespace stripped. Applies to CRLF, LF, and CR line endings. */
export function unfoldLines(text) {
  if (!text) return [];
  const raw = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const out = [];
  for (const line of raw) {
    if ((line.startsWith(" ") || line.startsWith("\t")) && out.length > 0) {
      out[out.length - 1] += line.slice(1);
    } else {
      out.push(line);
    }
  }
  return out;
}

/** Decode QUOTED-PRINTABLE segments (common in vCard 2.1 from old Nokia,
 * some Outlook exports). Handles soft line breaks (=CRLF) and =XX hex.
 * Multi-byte UTF-8 sequences (`=C3=AD` = "í") are decoded together so we
 * don't emit garbled single-byte chars ("Ã­"). */
function decodeQuotedPrintable(str) {
  const noSoftBreaks = str.replace(/=(?:\r\n|\n|\r)/g, "");
  // Collapse each consecutive =XX run into a single decodeURIComponent
  // pass so UTF-8 multi-byte sequences resolve to one character.
  return noSoftBreaks.replace(/(?:=[0-9A-Fa-f]{2})+/g, (run) => {
    const percent = run.replace(/=/g, "%").toUpperCase();
    try {
      return decodeURIComponent(percent);
    } catch {
      // Malformed sequence — fall back to raw bytes.
      let out = "";
      for (const m of run.matchAll(/=([0-9A-Fa-f]{2})/g)) {
        out += String.fromCharCode(parseInt(m[1], 16));
      }
      return out;
    }
  });
}

/** Split a vCard value line into { property, params[], value }.
 *  Example: `TEL;TYPE=CELL:+1 306 555 1234`
 *    → { property: "TEL", params: [["TYPE","CELL"]], value: "+1 306 555 1234" }
 *
 *  Handles quoted params (`TYPE="WORK,VOICE"`) and colons in the value. */
function parseLine(line) {
  if (!line) return null;
  // First unescaped colon separates the name+params block from the value.
  let colonIdx = -1;
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') inQuote = !inQuote;
    else if (ch === ":" && !inQuote) {
      colonIdx = i;
      break;
    }
  }
  if (colonIdx === -1) return null;
  const head = line.slice(0, colonIdx);
  let value = line.slice(colonIdx + 1);
  // Split head on unquoted semicolons.
  const parts = [];
  {
    let cur = "";
    inQuote = false;
    for (const ch of head) {
      if (ch === '"') inQuote = !inQuote;
      if (ch === ";" && !inQuote) {
        parts.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    if (cur) parts.push(cur);
  }
  const property = (parts.shift() || "").toUpperCase();
  const params = parts.map((p) => {
    const eq = p.indexOf("=");
    if (eq === -1) {
      // Bare param (vCard 2.1 style: `TEL;WORK;VOICE:...`).
      return ["TYPE", p.toUpperCase()];
    }
    const k = p.slice(0, eq).toUpperCase();
    let v = p.slice(eq + 1);
    if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
    return [k, v.toUpperCase()];
  });
  // Apply ENCODING/CHARSET before returning.
  const encoding = params.find(([k]) => k === "ENCODING")?.[1] || "";
  if (encoding === "QUOTED-PRINTABLE") {
    value = decodeQuotedPrintable(value);
  }
  return { property, params, value };
}

/** Does this record's params list have TYPE=<needle>? */
function hasType(params, needle) {
  const n = needle.toUpperCase();
  return params.some(([k, v]) => k === "TYPE" && v.split(",").includes(n));
}

/** Un-escape vCard text values: `\n` → newline, `\;` → `;`, `\,` → `,`. */
function unescapeText(value) {
  return (value || "")
    .replace(/\\n/gi, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

// ---------- Social URL detection ----------
// vCards from iOS/Android often bury social handles inside URL rows,
// X-SOCIALPROFILE rows, or NOTE. We match on host and channel keyword
// so `X-SOCIALPROFILE;TYPE=linkedin:...` and `URL;TYPE=twitter:https://x.com/foo`
// both land on the right form field.

const SOCIAL_HOST_MAP = [
  { field: "facebook_url", hosts: ["facebook.com", "fb.com", "fb.me"], keys: ["FACEBOOK", "FB"] },
  { field: "instagram_url", hosts: ["instagram.com"], keys: ["INSTAGRAM", "IG"] },
  { field: "linkedin_url", hosts: ["linkedin.com", "lnkd.in"], keys: ["LINKEDIN", "LI"] },
  { field: "tiktok_url", hosts: ["tiktok.com"], keys: ["TIKTOK", "TT"] },
  { field: "twitter_url", hosts: ["twitter.com", "x.com", "t.co"], keys: ["TWITTER", "X"] },
  { field: "youtube_url", hosts: ["youtube.com", "youtu.be"], keys: ["YOUTUBE", "YT"] },
  { field: "threads_url", hosts: ["threads.net", "threads.com"], keys: ["THREADS"] },
];

function classifyUrl(url, params) {
  const u = (url || "").trim();
  if (!u) return null;
  const lower = u.toLowerCase();
  const typeValues = params
    .filter(([k]) => k === "TYPE" || k === "X-SERVICE-TYPE" || k === "SERVICE" || k === "LABEL")
    .flatMap(([, v]) => v.split(","));
  for (const s of SOCIAL_HOST_MAP) {
    if (s.hosts.some((h) => lower.includes(h))) return s.field;
    if (typeValues.some((t) => s.keys.includes(t.toUpperCase()))) return s.field;
  }
  return null;
}

// ---------- Photo handling ----------
function photoValueToUrl(record) {
  // vCard 3.0: `PHOTO;ENCODING=b;TYPE=JPEG:<base64>`
  // vCard 4.0: `PHOTO:data:image/jpeg;base64,<base64>` OR `PHOTO:https://…`
  // Very old 2.1: `PHOTO;BASE64:<base64>` split over folded lines.
  const { params, value } = record;
  const v = (value || "").trim().replace(/\s+/g, "");
  if (!v) return "";
  const encoding = params.find(([k]) => k === "ENCODING")?.[1] || "";
  const isBase64 =
    encoding === "B" || encoding === "BASE64" || params.some(([k]) => k === "BASE64");
  if (v.startsWith("data:") || v.startsWith("http://") || v.startsWith("https://")) {
    return v;
  }
  if (isBase64) {
    const typeParam = params.find(([k]) => k === "TYPE")?.[1] || "JPEG";
    const mime = /PNG/i.test(typeParam)
      ? "image/png"
      : /GIF/i.test(typeParam)
      ? "image/gif"
      : /WEBP/i.test(typeParam)
      ? "image/webp"
      : "image/jpeg";
    return `data:${mime};base64,${v}`;
  }
  return v;
}

// ---------- Public API ----------

/** Parse a single vCard block (may include BEGIN:VCARD ... END:VCARD) into
 *  the shape of `blankRealtor()`. Fields the vCard doesn't mention are
 *  left blank; the caller merges over their existing form state.
 *
 *  Returns { fields, warnings, matched }:
 *    - fields:  the mapped record
 *    - warnings: human-readable warnings ("no name found", "multi-card file")
 *    - matched: array of field names actually filled (for the success toast)
 */
export function parseVcard(text) {
  const warnings = [];
  const source = String(text || "").trim();
  if (!source) {
    return { fields: {}, warnings: ["Empty file"], matched: [] };
  }
  const lines = unfoldLines(source);
  if (!lines.some((l) => /^BEGIN:VCARD/i.test(l))) {
    return { fields: {}, warnings: ["Not a vCard file"], matched: [] };
  }
  const cardCount = lines.filter((l) => /^BEGIN:VCARD/i.test(l)).length;
  if (cardCount > 1) {
    warnings.push(
      `File contains ${cardCount} contacts — only the first one was imported.`,
    );
  }

  // Grab the first BEGIN...END block.
  const startIdx = lines.findIndex((l) => /^BEGIN:VCARD/i.test(l));
  const endIdx = lines.findIndex(
    (l, i) => i > startIdx && /^END:VCARD/i.test(l),
  );
  const block = lines.slice(startIdx + 1, endIdx === -1 ? lines.length : endIdx);

  const fields = {};
  const matched = [];
  const setField = (key, val) => {
    if (!val && val !== 0) return;
    if (fields[key]) return; // first-wins so preferred phone/email lands first
    fields[key] = val;
    matched.push(key);
  };

  // Two-pass: N (structured name) beats FN if both present.
  let fn = "";
  let structuredFirst = "";
  let structuredLast = "";
  const socialCandidates = []; // urls that need host-based classification

  for (const line of block) {
    const rec = parseLine(line);
    if (!rec) continue;
    const { property, params, value } = rec;
    switch (property) {
      case "FN":
        fn = unescapeText(value).trim();
        break;
      case "N": {
        const parts = value.split(";");
        structuredLast = unescapeText(parts[0] || "").trim();
        structuredFirst = unescapeText(parts[1] || "").trim();
        break;
      }
      case "NICKNAME":
        setField("nickname", unescapeText(value).split(",")[0].trim());
        break;
      case "ORG": {
        // Multi-level ORG is `Company;Department;…`. First segment is the brokerage.
        const org = unescapeText(value).split(";")[0].trim();
        setField("brokerage_name", org);
        setField("business_name", org);
        break;
      }
      case "TITLE":
      case "ROLE":
        setField("title", unescapeText(value).trim());
        break;
      case "TEL": {
        const num = value.trim();
        if (!num) break;
        if (hasType(params, "WORK")) {
          if (!fields.work_phone) setField("work_phone", num);
          else if (!fields.phone) setField("phone", num);
        } else if (
          hasType(params, "CELL") ||
          hasType(params, "MOBILE") ||
          hasType(params, "IPHONE")
        ) {
          setField("phone", num);
        } else if (hasType(params, "HOME") || hasType(params, "VOICE")) {
          if (!fields.phone) setField("phone", num);
        } else {
          // Untyped — fall through to primary phone.
          if (!fields.phone) setField("phone", num);
        }
        break;
      }
      case "EMAIL":
        setField("email", value.trim());
        break;
      case "BDAY": {
        // Accept `1985-06-15`, `19850615`, `1985-06-15T00:00:00Z`.
        const raw = value.trim().replace(/[Tt].*/, "");
        const iso8 = /^(\d{4})(\d{2})(\d{2})$/.exec(raw);
        if (iso8) setField("birthday", `${iso8[1]}-${iso8[2]}-${iso8[3]}`);
        else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) setField("birthday", raw);
        break;
      }
      case "PHOTO":
      case "LOGO":
        setField("photo_url", photoValueToUrl({ params, value }));
        break;
      case "URL": {
        const url = value.trim();
        if (!url) break;
        const field = classifyUrl(url, params);
        if (field) setField(field, url);
        else if (!fields.profile_url) setField("profile_url", url);
        else socialCandidates.push(url);
        break;
      }
      case "X-SOCIALPROFILE":
      case "SOCIALPROFILE":
      case "X-URL":
      case "IMPP": {
        const url = value.trim();
        if (!url) break;
        const field = classifyUrl(url, params);
        if (field) setField(field, url);
        break;
      }
      case "NOTE":
        setField("notes", unescapeText(value).trim());
        break;
      default:
        break;
    }
  }

  // Prefer structured N, fall back to FN split.
  if (structuredFirst || structuredLast) {
    fields.first_name = structuredFirst;
    fields.last_name = structuredLast;
  } else if (fn) {
    const parts = fn.split(/\s+/);
    fields.first_name = parts[0] || "";
    fields.last_name = parts.slice(1).join(" ");
  }
  if (!fields.first_name && !fields.last_name) {
    warnings.push("Contact has no name — please fill it in manually.");
  }
  return { fields, warnings, matched: Array.from(new Set(matched)) };
}

/** Fuzzy-match a brokerage_name string to the caller's known brokerages
 *  list. Returns the matched brokerage's id, or "" if no confident match.
 *  Case-insensitive, ignores punctuation and common corporate suffixes so
 *  "Coldwell Banker Signature" matches "Coldwell Banker Signature Ltd." */
export function fuzzyMatchBrokerage(name, brokerages) {
  if (!name || !brokerages || brokerages.length === 0) return "";
  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\b(inc|ltd|llc|corp|corporation|realty|realtors?|limited|brokerage)\b\.?/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const needle = norm(name);
  if (!needle) return "";
  // Prefer exact-normalized match, then startsWith, then includes.
  for (const b of brokerages) {
    if (norm(b.name) === needle) return b.id;
  }
  for (const b of brokerages) {
    if (norm(b.name).startsWith(needle) || needle.startsWith(norm(b.name))) return b.id;
  }
  for (const b of brokerages) {
    if (norm(b.name).includes(needle) || needle.includes(norm(b.name))) return b.id;
  }
  return "";
}
